import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import cx from 'classnames'
import { useForm, Controller } from 'react-hook-form'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import DashboardTooltip from 'modules/Dashboard/components/shared/DashboardTooltip/DashboardTooltip'
import styles from './DownloadSettingsForm.module.scss'
import { isBefore } from 'date-fns'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { dataGroupingDefault, dataGroupingValues } from 'constants/DataGrouping'
import { AppDatePicker, toDateInputValue } from 'common/components/DatePicker'

const DOWNLOAD_STEP = {
  PARAMETERS: 'parameters',
  DATE_AND_FORMAT: 'dateAndFormat'
}

// The download backend rejects a request when its estimated processing time
// exceeds this many seconds (HTTP 413). We mirror that estimate here to show how
// many days can be downloaded for the current selection.
const EXECUTION_TIME_LIMIT_SECONDS = 27
const MAX_RANGE_DAYS = 3650

// Mirror of estimateExecutionTime in inbiot-private-api-device
// (inbiot_library/python/inbiot_algorithms.py) — keep both copies in sync.
// Estimates processing seconds from the number of rows (date range ÷ resolution)
// and columns (selected parameters), with different coefficients per file type.
const estimateExecutionTime = (numRows, numColumns, fileType) => {
  const r = numRows
  const c = numColumns
  const seconds =
    fileType === 'csv'
      ? -0.1271 +
        0.00004233 * r -
        0.05029 * c -
        9.692e-11 * r ** 2 +
        1.0217e-5 * r * c +
        0.003061 * c ** 2
      : 2.8026 +
        8.521e-5 * r -
        0.6329 * c -
        2.1942e-11 * r ** 2 +
        2.2568e-5 * r * c +
        0.04973 * c ** 2

  return Math.max(seconds, 0)
}

// Resolution (minutes between points) for the selected interval. The "original
// data" option uses the device's own measurement interval.
const getRangeResolutionMinutes = (intervalValue, timeInterval) => {
  if (intervalValue === dataGroupingDefault.value) {
    return timeInterval || 10
  }

  const grouping = dataGroupingValues.find(item => item.value === intervalValue)

  return grouping ? grouping.minutes : timeInterval || 10
}

// Largest number of days whose estimated processing time stays within the
// backend limit, for the selected parameters / interval / file type.
export const getAvailableRangeDays = ({ numParams, intervalValue, timeInterval, fileType }) => {
  if (!numParams) return 0

  const rowsPerDay = (24 * 60) / getRangeResolutionMinutes(intervalValue, timeInterval)

  let days = 0
  while (
    estimateExecutionTime((days + 1) * rowsPerDay, numParams, fileType) <=
    EXECUTION_TIME_LIMIT_SECONDS
  ) {
    days += 1
    if (days >= MAX_RANGE_DAYS) break
  }

  return days
}

const selectMenuProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null,
  MenuListProps: {
    className: styles.selectMenuList
  },
  PaperProps: {
    className: styles.selectMenuPaper
  }
}

const DownloadSettingsSelect = ({ children, id, onChange, renderValue, value }) => (
  <FormControl>
    <Select
      id={id}
      value={value}
      displayEmpty
      renderValue={renderValue}
      onChange={onChange}
      variant='standard'
      disableUnderline
      className={styles.select}
      IconComponent={KeyboardArrowDownIcon}
      classes={{
        icon: styles.selectIcon,
        select: styles.selectFocus
      }}
      MenuProps={selectMenuProps}
    >
      {children}
    </Select>
  </FormControl>
)

const DownloadSettingsMenuItem = ({ children, value }) => (
  <MenuItem
    value={value}
    className={styles.menuItem}
    classes={{
      selected: styles.menuItemSelected
    }}
  >
    {children}
  </MenuItem>
)

const DownloadStepNav = ({ activeStep, translate }) => (
  <div className={styles.nav}>
    <Typography
      className={
        activeStep === DOWNLOAD_STEP.PARAMETERS
          ? styles.subheaderParamsSelected
          : styles.subheaderParams
      }
    >
      {translate('editNavParams')}
    </Typography>
    <Typography
      className={
        activeStep === DOWNLOAD_STEP.DATE_AND_FORMAT
          ? styles.subheaderDateandFormatSelected
          : styles.subheaderDateandFormat
      }
    >
      {translate('navDateAndFormat')}
    </Typography>
  </div>
)

const DownloadParametersStep = ({
  errors,
  onCancel,
  onNext,
  onSelectAll,
  onTypeChange,
  selectAllCheckboxRef,
  translate,
  typeChecked,
  types
}) => (
  <div className={styles.firstPage}>
    <DownloadStepNav
      activeStep={DOWNLOAD_STEP.PARAMETERS}
      translate={translate}
    />
    <div className={styles.subInfoWrapper}>
      <Typography className={styles.subInfo}>
        {translate('downloadDataForm.selectParameters')}
      </Typography>
    </div>
    <FormControlLabel
      className={styles.selectAllCheckboxForm}
      control={
        <input
          checked={types.length > 0 && typeChecked.length === types.length}
          className={styles.checkbox}
          name='selectAll'
          id='selectAll'
          onChange={onSelectAll}
          ref={selectAllCheckboxRef}
          type='checkbox'
        />
      }
      label={translate('downloadDataForm.selectAll')}
    />
    <div className={styles.checkboxWrapper}>
      {types.map((dataType, i) => (
        <div
          className={styles.checkboxFormDiv}
          key={dataType}
        >
          <FormControlLabel
            className={styles.checkboxForm}
            control={
              <input
                checked={typeChecked.includes(dataType)}
                className={styles.checkbox}
                onChange={onTypeChange}
                required
                name={`types[${i}]`}
                value={dataType}
                type='checkbox'
              />
            }
            label={translate(dataType)}
          />
        </div>
      ))}
    </div>
    {errors.types && (
      <div className={styles.error}>
        <small>{translate('validation.anyFieldRequired')}</small>
      </div>
    )}
    <div className={styles.buttonWrapper1}>
      <Button
        fullWidth
        variant='contained'
        className={styles.buttonSiguiente}
        onClick={onNext}
      >
        {translate('next')}
        <ArrowForward className={styles.arrow} />
      </Button>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onCancel}
      >
        {translate('cancel')}
      </Button>
    </div>
  </div>
)

const DownloadDateAndFormatStep = ({
  availableRangeDays,
  control,
  errors,
  fileExtensionValue,
  getDownloadMinDate,
  getFileExtensionLabel,
  getGroupingLabel,
  getOrderLabel,
  groupingValue,
  isPremium,
  isSubmitting,
  onBack,
  onCancel,
  onChangeFileExtension,
  onChangeGrouping,
  onChangeOrder,
  orderValue,
  submitError,
  system,
  translate,
  validateDates
}) => (
  <div className={styles.secondPage}>
    <DownloadStepNav
      activeStep={DOWNLOAD_STEP.DATE_AND_FORMAT}
      translate={translate}
    />
    <div className={styles.dataGroupingWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.dataGrouping')}
      </Typography>
      <DownloadSettingsSelect
        id='dataGrouping'
        value={groupingValue}
        renderValue={getGroupingLabel}
        onChange={onChangeGrouping}
      >
        <DownloadSettingsMenuItem value={dataGroupingDefault.value}>
          {translate(dataGroupingDefault.translation)}
        </DownloadSettingsMenuItem>
        {dataGroupingValues
          .filter(item => system.timeInterval <= item.minutes)
          .map(item => (
            <DownloadSettingsMenuItem
              key={item.value}
              value={item.value}
            >
              {translate(item.translation)}
            </DownloadSettingsMenuItem>
          ))}
      </DownloadSettingsSelect>
    </div>
    <div>
      <Typography className={styles.subInfo}>
        {translate('downloadDataForm.selectDateInterval')}
      </Typography>
    </div>
    <DashboardTooltip
      arrow
      label={!isPremium ? translate('businessPlan') : ''}
      placement='right'
    >
      <div className={styles.getMoreRange}>
        {translate('downloadDataForm.availableRange')}
        {` ${availableRangeDays} ${translate('downloadDataForm.availableRangeDaysUnit')}`}
        <span className={styles.infoIcon}>i</span>
      </div>
    </DashboardTooltip>
    <div className={styles.datePickerWrapper}>
      <div className={styles.picker}>
        <Controller
          control={control}
          name='startDate'
          defaultValue=''
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AppDatePicker
              disableFuture
              error={Boolean(errors.startDate)}
              inputClassName={styles.datePicker}
              label={translate('startDate')}
              min={getDownloadMinDate()}
              name='startDatePicker'
              onChange={onChange}
              required
              value={value}
            />
          )}
        />
      </div>
      {errors.startDate && (
        <div className={styles.error}>
          <small>{translate('validation.required')}</small>
        </div>
      )}
      <div className={styles.picker}>
        <Controller
          control={control}
          name='endDate'
          defaultValue=''
          rules={{ required: true, validate: validateDates }}
          render={({ field: { onChange, value } }) => (
            <AppDatePicker
              disableFuture
              error={Boolean(errors.endDate)}
              inputClassName={styles.datePicker}
              label={translate('endDate')}
              min={getDownloadMinDate()}
              name='endDatePicker'
              onChange={onChange}
              required
              value={value}
            />
          )}
        />
      </div>
      {errors.endDate && errors.endDate?.type === 'required' && (
        <div className={styles.error}>
          <small>{translate('validation.required')}</small>
        </div>
      )}
      {errors.endDate && errors.endDate?.type === 'validate' && (
        <div className={styles.error}>
          <small>{translate('validation.invalidDateRange')}</small>
        </div>
      )}
    </div>
    <div className={styles.fileExtensionWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.downloadOrder')}
      </Typography>
      <DownloadSettingsSelect
        id='orderSelect'
        value={orderValue}
        renderValue={getOrderLabel}
        onChange={onChangeOrder}
      >
        <DownloadSettingsMenuItem value='0'>
          {translate('downloadDataForm.olderDataFirst')}
        </DownloadSettingsMenuItem>
        <DownloadSettingsMenuItem value='1'>
          {translate('downloadDataForm.latestDataFirst')}
        </DownloadSettingsMenuItem>
      </DownloadSettingsSelect>
    </div>
    <div className={styles.fileExtensionWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.downloadExtension')}
      </Typography>
      <DownloadSettingsSelect
        id='extensionFileSelect'
        value={fileExtensionValue}
        renderValue={getFileExtensionLabel}
        onChange={onChangeFileExtension}
      >
        <DownloadSettingsMenuItem value='csv'>{translate('csv')}</DownloadSettingsMenuItem>
        <DownloadSettingsMenuItem value='xlsx'>{translate('xlsx')}</DownloadSettingsMenuItem>
      </DownloadSettingsSelect>
    </div>
    <div className={cx(styles.buttonWrapper, styles.downloadButtonWrapper)}>
      {submitError && <div className={styles.noDataMessage}>{submitError}</div>}
      <LoadingButton
        type='submit'
        fullWidth
        loading={isSubmitting}
        loadingPosition='end'
        variant='contained'
        className={styles.buttonSiguiente}
      >
        {translate('download')}
      </LoadingButton>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        disabled={isSubmitting}
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onBack}
      >
        <ArrowBack className={styles.arrow} />
        {translate('back')}
      </Button>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        disabled={isSubmitting}
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onCancel}
      >
        {translate('cancel')}
      </Button>
    </div>
  </div>
)

const DownloadSettingsForm = props => {
  const { onSubmit, translate, system, sortedData, isPremium, submitError } = props
  const { handleSubmit, control, setError, clearErrors, formState, getValues } = useForm()
  // v7 moved errors inside formState (was a top-level useForm return in v6).
  const { errors, isSubmitting } = formState

  const [value, setValue] = useState('csv')
  const [orderValue, setOrderValue] = useState('0')
  const [groupingValue, setGroupingValue] = useState('c')
  const [activeStep, setActiveStep] = useState(DOWNLOAD_STEP.PARAMETERS)
  const [typeChecked, setTypeChecked] = useState([])
  const selectAllCheckboxRef = useRef(null)

  const types = useMemo(() => {
    const dataTypes = sortedData.map(item => item.type)

    if (system.weather && system.weather.baseStationLocation) {
      dataTypes.push('weather')
    }

    return dataTypes
  }, [sortedData, system])

  const selectedTypes = types.filter(dataType => typeChecked.includes(dataType))
  const numParams = selectedTypes.filter(dataType => dataType !== 'weather').length

  const availableRangeDays = useMemo(
    () =>
      getAvailableRangeDays({
        numParams,
        intervalValue: groupingValue,
        timeInterval: system.timeInterval,
        fileType: value
      }),
    [numParams, groupingValue, system.timeInterval, value]
  )

  useEffect(() => {
    setTypeChecked(previousTypeChecked => {
      const nextTypeChecked = previousTypeChecked.filter(dataType => types.includes(dataType))

      return nextTypeChecked.length === previousTypeChecked.length
        ? previousTypeChecked
        : nextTypeChecked
    })
  }, [types])

  const handleChangeOrder = event => {
    setOrderValue(event.target.value)
  }

  const handleChangeFileExtension = event => {
    setValue(event.target.value)
  }

  const handleChangeGrouping = event => {
    setGroupingValue(event.target.value)
  }

  const getGroupingLabel = selectedValue => {
    const groupingOptions = [dataGroupingDefault, ...dataGroupingValues]
    const groupingOption = groupingOptions.find(item => item.value === selectedValue)

    return groupingOption ? translate(groupingOption.translation) : selectedValue
  }

  const getOrderLabel = selectedValue => {
    if (selectedValue === '0') return translate('downloadDataForm.olderDataFirst')
    if (selectedValue === '1') return translate('downloadDataForm.latestDataFirst')
    return selectedValue
  }

  const getFileExtensionLabel = selectedValue => translate(selectedValue)

  const setTypeSelectionError = nextTypeChecked => {
    if (nextTypeChecked.length === 0) {
      setError('types', { type: 'required' })
    } else {
      clearErrors('types')
    }
  }

  const handleChange = event => {
    if (event.target.type === 'radio') {
      setValue(event.target.value)
      return
    }

    if (event.target.type !== 'checkbox') return

    const { checked, value } = event.target
    const nextTypeChecked = checked
      ? [...new Set([...typeChecked, value])]
      : typeChecked.filter(item => item !== value)

    setTypeChecked(nextTypeChecked)
    setTypeSelectionError(nextTypeChecked)
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const handleSelectAll = () => {
    const nextTypeChecked = typeChecked.length === types.length ? [] : types

    setTypeChecked(nextTypeChecked)
    setTypeSelectionError(nextTypeChecked)
  }

  const checkInputs = () => {
    if (isBefore(new Date(getValues('endDate')), new Date(getValues('startDate'))))
      return 'Invalid date'

    const data = {}
    data.weather = selectedTypes.includes('weather')
    data.params = selectedTypes.filter(dataType => dataType !== 'weather')
    data.endDate = getValues('endDate')
    data.startDate = getValues('startDate')
    data.fileType = value
    data.order = orderValue
    data.interval = groupingValue

    return data
  }

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate =
        typeChecked.length > 0 && typeChecked.length !== types.length
    }
  }, [typeChecked.length, types.length])

  const onClickNext = () => {
    if (selectedTypes.length !== 0) {
      setActiveStep(DOWNLOAD_STEP.DATE_AND_FORMAT)
    } else {
      setError('types', { type: 'required' })
    }
  }

  const onClickBack = () => {
    setActiveStep(DOWNLOAD_STEP.PARAMETERS)
  }

  const onClickDownload = () => {
    const data = checkInputs()
    if (data === 'Invalid date') return
    return onSubmit(data)
  }

  const getDownloadMinDate = () => {
    if (isPremium) return undefined

    const date = new Date()
    date.setDate(date.getDate() - 30)

    return toDateInputValue(date)
  }

  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('downloadData')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={handleSubmit(onClickDownload)}
        >
          <div style={{ display: activeStep === DOWNLOAD_STEP.PARAMETERS ? 'block' : 'none' }}>
            <DownloadParametersStep
              errors={errors}
              onCancel={onClickCancel}
              onNext={onClickNext}
              onSelectAll={handleSelectAll}
              onTypeChange={handleChange}
              selectAllCheckboxRef={selectAllCheckboxRef}
              translate={translate}
              typeChecked={typeChecked}
              types={types}
            />
          </div>
          <div
            style={{
              display: activeStep === DOWNLOAD_STEP.DATE_AND_FORMAT ? 'block' : 'none'
            }}
          >
            <DownloadDateAndFormatStep
              availableRangeDays={availableRangeDays}
              control={control}
              errors={errors}
              fileExtensionValue={value}
              getDownloadMinDate={getDownloadMinDate}
              getFileExtensionLabel={getFileExtensionLabel}
              getGroupingLabel={getGroupingLabel}
              getOrderLabel={getOrderLabel}
              groupingValue={groupingValue}
              isPremium={isPremium}
              isSubmitting={isSubmitting}
              onBack={onClickBack}
              onCancel={onClickCancel}
              onChangeFileExtension={handleChangeFileExtension}
              onChangeGrouping={handleChangeGrouping}
              onChangeOrder={handleChangeOrder}
              orderValue={orderValue}
              submitError={submitError}
              system={system}
              translate={translate}
              validateDates={checkInputs}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

DownloadSettingsForm.propTypes = {
  system: PropTypes.instanceOf(Map),
  translate: PropTypes.func.isRequired
}

export default withLocalize(DownloadSettingsForm)
